import { Trash2, Building2, Mail, Phone, Inbox } from "lucide-react";

export const STATUS_STYLES = {
    new: "bg-terracotta/15 text-terracottadark border-terracotta/30",
    contacted: "bg-sage/10 text-sage border-sage/30",
    closed: "bg-stone2 text-inksoft border-linec",
};

export function StatCard({ label, value, accent }) {
    return (
        <div className="border border-linec rounded-sm p-6 bg-cream">
            <div className={`font-heading text-4xl ${accent ? "text-terracotta" : "text-sage"}`}>
                {value}
            </div>
            <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-inksoft mt-2">
                {label}
            </div>
        </div>
    );
}

function DetailField({ icon: Icon, label, value, link }) {
    return (
        <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-inksoft mb-1 flex items-center gap-1.5">
                {Icon && <Icon size={12} />} {label}
            </div>
            {link ? (
                <a href={link} className="text-ink hover:text-terracotta transition-colors break-words">
                    {value}
                </a>
            ) : (
                <div className="text-ink break-words">{value}</div>
            )}
        </div>
    );
}

const StatusBadge = ({ status }) => (
    <span className={`inline-block text-[11px] font-mono uppercase tracking-wide px-2.5 py-1 rounded-sm border ${STATUS_STYLES[status]}`}>
        {status}
    </span>
);

export function LeadsEmptyState() {
    return (
        <div className="border border-linec rounded-sm p-16 text-center" data-testid="empty-state">
            <Inbox size={40} className="mx-auto text-linec" />
            <p className="font-heading text-2xl text-ink mt-4">No leads yet</p>
            <p className="text-inksoft mt-2 text-sm">
                Submissions from the consultation form will appear here.
            </p>
        </div>
    );
}

export function LeadsTable({ leads, onSelect }) {
    return (
        <div className="border border-linec rounded-sm overflow-hidden bg-cream">
            <div className="hidden md:grid grid-cols-[1.3fr_1.3fr_1fr_0.8fr_0.9fr] gap-4 px-6 py-4 bg-stone2/60 border-b border-linec font-mono text-[10px] uppercase tracking-[0.16em] text-inksoft">
                <span>Practice</span>
                <span>Contact</span>
                <span>PM System</span>
                <span>Status</span>
                <span>Received</span>
            </div>
            {leads.map((lead) => (
                <button
                    key={lead.id}
                    onClick={() => onSelect(lead)}
                    data-testid={`lead-row-${lead.id}`}
                    className="w-full text-left grid md:grid-cols-[1.3fr_1.3fr_1fr_0.8fr_0.9fr] gap-1 md:gap-4 px-6 py-4 border-b border-linec last:border-0 hover:bg-stone2/40 transition-colors items-center"
                >
                    <span className="font-medium text-ink">{lead.practice_name}</span>
                    <span className="text-sm text-inksoft">
                        {lead.contact_name}
                        <span className="block text-xs text-inksoft/70">{lead.email}</span>
                    </span>
                    <span className="text-sm text-inksoft">{lead.pm_system || "—"}</span>
                    <span><StatusBadge status={lead.status} /></span>
                    <span className="text-xs text-inksoft">
                        {new Date(lead.created_at).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                        })}
                    </span>
                </button>
            ))}
        </div>
    );
}

export function LeadDetailPanel({ lead, onClose, onChangeStatus, onDelete }) {
    return (
        <div
            className="fixed inset-0 z-50 flex justify-end"
            onClick={onClose}
            data-testid="lead-detail-overlay"
        >
            <div className="absolute inset-0 bg-sagedark/40 backdrop-blur-sm" />
            <div
                className="relative w-full max-w-md bg-cream h-full overflow-y-auto border-l border-linec p-8"
                onClick={(e) => e.stopPropagation()}
                data-testid="lead-detail-panel"
            >
                <div className="flex items-start justify-between">
                    <div>
                        <StatusBadge status={lead.status} />
                        <h2 className="font-heading text-3xl text-ink mt-4">{lead.practice_name}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="close-detail-button"
                        className="text-inksoft hover:text-ink text-2xl leading-none"
                    >
                        ×
                    </button>
                </div>

                <div className="mt-8 space-y-5">
                    <DetailField icon={Building2} label="Contact" value={lead.contact_name} />
                    <DetailField icon={Mail} label="Email" value={lead.email} link={`mailto:${lead.email}`} />
                    <DetailField icon={Phone} label="Phone" value={lead.phone} link={`tel:${lead.phone}`} />
                    <div className="grid grid-cols-2 gap-4">
                        <DetailField label="Providers" value={lead.num_providers || "—"} />
                        <DetailField label="PM System" value={lead.pm_system || "—"} />
                    </div>
                    {lead.message && (
                        <div>
                            <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-inksoft mb-2">
                                Message
                            </div>
                            <p className="text-sm text-ink leading-relaxed bg-stone2/50 border border-linec rounded-sm p-4">
                                {lead.message}
                            </p>
                        </div>
                    )}
                    <DetailField label="Received" value={new Date(lead.created_at).toLocaleString()} />
                </div>

                <div className="mt-8 pt-6 border-t border-linec">
                    <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-inksoft mb-3">
                        Update status
                    </div>
                    <div className="flex gap-2">
                        {["new", "contacted", "closed"].map((s) => (
                            <button
                                key={s}
                                onClick={() => onChangeStatus(lead.id, s)}
                                data-testid={`status-${s}-button`}
                                className={`flex-1 text-sm py-2.5 rounded-sm border transition-colors capitalize ${
                                    lead.status === s
                                        ? "bg-sage text-cream border-sage"
                                        : "border-linec text-inksoft hover:bg-stone2"
                                }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => onDelete(lead.id)}
                        data-testid="delete-lead-button"
                        className="mt-4 w-full inline-flex items-center justify-center gap-2 text-sm text-destructive border border-destructive/30 rounded-sm py-2.5 hover:bg-destructive/5 transition-colors"
                    >
                        <Trash2 size={15} /> Delete lead
                    </button>
                </div>
            </div>
        </div>
    );
}
